import { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import {
  UniversalFilterInvoiceProcessingJobStatusStatus,
  UniversalFilterInvoiceProcessingJobStatusValue,
  UniversalFilterInvoiceProcessingJobStatusModes,
  UniversalFilterInvoiceProcessingJobStatusFilterState,
  UniversalFilterInvoiceProcessingJobStatusOnChange
} from '../../UniversalFilterInvoiceProcessingJobStatus.types';

interface UniversalFilterInvoiceProcessingJobStatusFilterStateOptions {
  onChange: UniversalFilterInvoiceProcessingJobStatusOnChange;
}

function useUniversalFilterInvoiceProcessingJobStatusFilterState({
  onChange
}: UniversalFilterInvoiceProcessingJobStatusFilterStateOptions) {
  // All modes and options available in handleApplyFilterState
  // must be implemented in handleParseFilterValue too
  const handleApplyFilterState = useCallback<
    (filterState: UniversalFilterInvoiceProcessingJobStatusFilterState) => void
  >(
    (filterState) => {
      const statuses = filterState?.statuses;

      if (
        filterState?.mode ===
        UniversalFilterInvoiceProcessingJobStatusModes.IS_ANY_OF
      ) {
        onChange(isEmpty(statuses) ? undefined : { in: statuses });
        return;
      }

      if (
        filterState?.mode ===
        UniversalFilterInvoiceProcessingJobStatusModes.IS_NONE_OF
      ) {
        onChange(isEmpty(statuses) ? undefined : { notIn: statuses });
        return;
      }

      onChange();
    },
    [onChange]
  );

  const handleParseFilterValue = useCallback<
    (
      filterValue: UniversalFilterInvoiceProcessingJobStatusValue
    ) => UniversalFilterInvoiceProcessingJobStatusFilterState | undefined
  >((filterValue) => {
    if (isNil(filterValue)) {
      return undefined;
    }

    if (!isEmpty(filterValue.in)) {
      return {
        mode: UniversalFilterInvoiceProcessingJobStatusModes.IS_ANY_OF,
        statuses:
          filterValue.in as UniversalFilterInvoiceProcessingJobStatusStatus[]
      };
    }

    if (!isEmpty(filterValue.notIn)) {
      return {
        mode: UniversalFilterInvoiceProcessingJobStatusModes.IS_NONE_OF,
        statuses:
          filterValue.notIn as UniversalFilterInvoiceProcessingJobStatusStatus[]
      };
    }

    return undefined;
  }, []);

  return { handleApplyFilterState, handleParseFilterValue };
}

export default useUniversalFilterInvoiceProcessingJobStatusFilterState;
